import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { debounce, isEqual } from "lodash";
import { 
    fetchCreatePartyData, fetchPartySuggestions, resetPartySuggestions, 
    saveAssociatedParty, editAssociatedParty, resetEditPartyData, 
    fetchPartyStates, fetchCarrierLists 
} from '../actions/action';
import Icon from '../../../icons';
import { 
    NAME_REQUIRED_ERROR, PARTIES_WITHOUT_ADDRESS, TYPE_REQUIRED_ERROR, 
    RELATION_REQUIRED_ERROR, EMAIL_ERROR, WEBSITE_ERROR, 
    WORKKARD_CHECKBOX_REQUIRED_ERROR, ACTIVITY_CHECKBOX_REQUIRED_ERROR, 
    STATE_REQUIRED_ERROR, ZIP_REQUIRED_ERROR, CITY_REQUIRED_ERROR, 
    ADDRESS_REQUIRED_ERROR, PARTIES_WITH_TAXID, TAXID_REQUIRED_ERROR, 
    SPECIALCHARACTER_NAME_ERROR 
} from './constants';
import { validatePhone, validateEmail, validateUrl, validateSpecialCharString } from '../../../common/util';
import ConfirmationTextBox from "../../../components/ConfirmationPrompt";
import { showDialogueBox, hideDialogueBox } from '../../../components/DialogueBox/actions';
import SelectSearch from "../../../components/SelectSearch";
import LoaderComponent from '../../../components/Loader';
import CheckBox from '../../../components/Checkbox';
import { countriesData, partyStatesData } from '@/common/constants';

const SUPPORTED_COUNTRIES = new Set(['CA', 'GR', 'IN', 'KE', 'GB', 'US']);
const EMPTY_PHONE = { id: 0, phone_type: "", phone: "", numberError: "", typeError: "" };

const CreateParty = ({ editId, isAdminPanel, closeForm, onSuccess, associateWith = 'workkard' }) => {
    const dispatch = useDispatch();
    
    const partySuggestionRef = useRef(null);
    const contactSuggestionRef = useRef(null);
    const originalPayloadRef = useRef(null);
    const isInitialized = useRef(false);

    const {
        getPartyData,
        createPartyData, 
        carrierList, 
        partySuggestions, 
        workkardId, 
        activityId, 
        formData 
    } = useSelector(state => ({
        getPartyData: state.adminPanel.parties.partiesData.results,
        createPartyData: state.header.associatedParties.createPartyData || {},
        carrierList: state.header.associatedParties.carrierList,
        partySuggestions: state.header.associatedParties.partySuggestions,
        workkardId: state.workkardDetail.workkardDetail.id,
        activityId: state.workkardDetail.activityDetail.activityId,
        formData: state.header.associatedParties.editPartyData,
    }), shallowEqual);

    const [state, setState] = useState({
        buttonLoading: false,
        partyId: null,
        is_global: !isAdminPanel,
        organization_name: "",
        legal_name: "",
        contact_name: "",
        party_type: "",
        contact_relationship: "",
        party_entity_type: "",
        tax_id: "",
        account_number: "",
        addToMyParty: true,
        partyEntity: false,
        mailing_address1: "",
        mailing_address2: "",
        mailing_city: "",
        mailing_country: 'US',
        mailing_zip: "",
        billing_address1: "",
        billing_address2: "",
        billing_city: "",
        billing_country: 'US',
        billing_zip: "",
        billing_address_is_same: true,
        email: "",
        website: "",
        showPartySuggestions: false,
        showContactSuggestions: false,
        addressExpanded: false,
        phoneExpanded: false,
        webExpanded: false,
        nameRequiredError: false,
        orgNameError: false,
        legalNameError: false,
        contactNameError: false,
        taxIdError: false,
        typeRequiredError: false,
        relationRequiredError: false,
        checkboxError: false,
        emailError: false,
        websiteError: false,
        addressError: false,
        cityError: false,
        stateError: false,
        zipError: false,
        disableContactRelation: true,
        touched: false,
        mailing_state: '',
        selectTitle: 'Select State',
        billing_state: '',
        selectBillingTitle: 'Select State',
        carrier_id: "",
        party_source: "",
        phone_details: [{ ...EMPTY_PHONE }, { ...EMPTY_PHONE }]
    });

    const isCountryWithStates = useCallback((country) => SUPPORTED_COUNTRIES.has(country), []);
    
    const updateState = useCallback((updates) => {
        setState(prev => {
            const hasChange = Object.keys(updates).some(key => prev[key] !== updates[key]);
            if (!hasChange) return prev; 
            return { ...prev, ...updates };
        });
    }, []);

    // Initial Data Fetch
    useEffect(() => {
        dispatch(fetchCreatePartyData());
        dispatch(fetchCarrierLists());
        dispatch(fetchPartyStates({ key: '', zipCode: null, country: 'US', listType: 2 }));
        
        const onClickOutSideSuggestion = (e) => {
            if (partySuggestionRef.current && !partySuggestionRef.current.contains(e.target)) {
                updateState({ showPartySuggestions: false });
            }
            if (contactSuggestionRef.current && !contactSuggestionRef.current.contains(e.target)) {
                updateState({ showContactSuggestions: false });
            }
        };

        document.addEventListener('mousedown', onClickOutSideSuggestion);
        return () => {
            document.removeEventListener('mousedown', onClickOutSideSuggestion);
            dispatch(resetEditPartyData());
            dispatch(resetPartySuggestions());
        };
    }, [dispatch, updateState]);

    useEffect(() => {
        isInitialized.current = false;
    }, [editId]);

    // Prefill logic 
    useEffect(() => {
        if (editId && getPartyData?.length > 0 && !isInitialized.current) {
            const party = getPartyData.find(_val => _val.party_id == editId);
            if (party) {
                const originalPhoneDetails = party.phone_details?.length
                    ? party.phone_details.map(p => ({
                        id: p.id || 0,
                        phone_type: p.phone_type ? p.phone_type.charAt(0).toUpperCase() + p.phone_type.slice(1).toLowerCase() : "",
                        phone: p.phone || "",
                        numberError: "",
                        typeError: ""
                    }))
                    : [{ ...EMPTY_PHONE }, { ...EMPTY_PHONE }];

                const partyData = {
                    carrier_id: party.carrier_id || "",
                    party_type: party.party_type || "",
                    legal_name: party.legal_name || "",
                    contact_name: party.contact_name || "",
                    organization_name: party.organization_name || "",
                    party_source: party.party_source || "",
                    party_entity_type: party.party_entity_type || "",
                    email: party.email || "",
                    website: party.website || "",
                    is_global: !party.is_global,
                    contact_relationship: party.contact_relationship || "",
                    mailing_address1: party.mailing_address1 || "",
                    mailing_address2: party.mailing_address2 || "",
                    mailing_city: party.mailing_city || "",
                    mailing_state: party.mailing_state || "",
                    mailing_zip: party.mailing_zip || "",
                    mailing_country: party.mailing_country || "US",
                    billing_address_is_same: party.billing_address_is_same ?? true,
                    billing_address1: party.billing_address1 || "",
                    billing_address2: party.billing_address2 || "",
                    billing_city: party.billing_city || "",
                    billing_state: party.billing_state || "",
                    billing_zip: party.billing_zip || "",
                    billing_country: party.billing_country || "",
                    tax_id: party.tax_id || "",
                    account_number: party.account_number || "",
                    phone_details: originalPhoneDetails
                };
                updateState(partyData);
                originalPayloadRef.current = partyData;
                isInitialized.current = true;
            }
        }
    }, [editId, getPartyData, updateState]);

    const fetchSuggestionsDebounced = useMemo(() =>
        debounce((name, value) => {
            if (value.trim()) {
                dispatch(fetchPartySuggestions({
                    q: value.trim(),
                    isContactName: name === "contact_name",
                    _: Date.now()
                }));
            } else {
                dispatch(resetPartySuggestions());
            }
        }, 500),
        [dispatch]
    );

    useEffect(() => {
        return () => fetchSuggestionsDebounced.cancel();
    }, [fetchSuggestionsDebounced]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        const updates = { [name]: value, touched: true };

        if (!editId && (name === "organization_name" || name === "contact_name")) {
            fetchSuggestionsDebounced(name, value);
        }

        if (name === "party_type") {
            const selected = createPartyData.partyTypes?.find(t => t.value == value)?.value;
            if (PARTIES_WITHOUT_ADDRESS.includes(selected)) {
                Object.assign(updates, { addressError: false, cityError: false, stateError: false, addressExpanded: false, zipError: false });
            }
            if (!PARTIES_WITH_TAXID.includes(selected)) updates.taxIdError = false;
            
            if (["Alternate Contact", "Lienholder", "Other"].includes(selected)) {
                updates.disableContactRelation = false;
            } else {
                updates.disableContactRelation = true;
                updates.contact_relationship = '';
            }
        }

        if (name === 'mailing_country' || name === 'billing_country') {
            const isMailing = name === 'mailing_country';
            if (isCountryWithStates(value)) {
                dispatch(fetchPartyStates({
                    key: '', zipCode: null, country: value,
                    listType: isMailing ? 0 : 1,
                    callback: (data) => {
                        if (data.listType === 0 && !data.states.some(x => x.Value === state.mailing_state)) {
                            updateState({ mailing_state: "", selectTitle: "Select State" });
                        } else if (data.listType === 1 && !data.states.some(x => x.Value === state.billing_state)) {
                            updateState({ billing_state: "", selectBillingTitle: "Select State" });
                        }
                    }
                }));
            } else {
                updates[isMailing ? 'mailing_state' : 'billing_state'] = '';
            }
        }

        updateState(updates);
    }, [editId, createPartyData.partyTypes, fetchSuggestionsDebounced, isCountryWithStates, dispatch, state.mailing_state, state.billing_state, updateState]);

    const handleCheckBoxChange = useCallback((e) => {
        updateState({ [e.id]: !state[e.id], touched: true });
    }, [state, updateState]);

    const handleChangePhone = useCallback((e, i) => {
        const { name, value } = e.target;
        const updatedPhones = [...state.phone_details];

        updatedPhones[i] = {
            ...updatedPhones[i],
            [name]: value,
            ...(name === "phone_type" ? { typeError: "" } : { numberError: "" })
        };

        updateState({ phone_details: updatedPhones, touched: true });
    }, [state.phone_details, updateState]);

    const validate = useCallback(() => {
        let isValid = true;
        const updates = {};
        const currentType = createPartyData?.partyTypes?.find(t => t.value == state.party_type)?.value || '';

        if (!state.organization_name.trim() && !state.contact_name.trim()) { updates.nameRequiredError = true; isValid = false; }
        if (state.organization_name && !validateSpecialCharString(state.organization_name)) { updates.orgNameError = true; isValid = false; }
        if (state.contact_name && !validateSpecialCharString(state.contact_name)) { updates.contactNameError = true; isValid = false; }
        
        if (!state.party_type || state.party_type === "0") { updates.typeRequiredError = true; isValid = false; }
        else if (!state.disableContactRelation && !state.contact_relationship) { updates.relationRequiredError = true; isValid = false; }

        if (PARTIES_WITH_TAXID.includes(currentType) && !state.tax_id.trim()) { updates.taxIdError = true; isValid = false; }

        if (!PARTIES_WITHOUT_ADDRESS.includes(currentType)) {
            if (!state.mailing_address1.trim()) { updates.addressError = true; updates.addressExpanded = true; isValid = false; }
            if (!state.mailing_city.trim()) { updates.cityError = true; updates.addressExpanded = true; isValid = false; }
            if (!state.mailing_state.trim()) { updates.stateError = true; updates.addressExpanded = true; isValid = false; }
            if (!state.mailing_zip.trim()) { updates.zipError = true; updates.addressExpanded = true; isValid = false; }
        }

        const validatedPhones = state.phone_details.map((p, i) => {
            const phone = { ...p };
            if (phone.phone_type && !phone.phone.trim()) {
                phone.numberError = "Required field."; isValid = false;
            } else if (phone.phone.trim() && !phone.phone_type) {
                phone.typeError = `Type ${i+1} required.`; isValid = false;
            } else if (phone.phone.trim() && !validatePhone(phone.phone)) {
                phone.numberError = "Invalid phone."; isValid = false;
            }
            return phone;
        });

        if (state.email && !validateEmail(state.email)) { updates.emailError = true; updates.webExpanded = true; isValid = false; }
        if (state.website && !validateUrl(state.website)) { updates.websiteError = true; updates.webExpanded = true; isValid = false; }
        if (!state.addToMyParty && !state.partyEntity) { updates.checkboxError = true; isValid = false; }

        updateState({ ...updates, phone_details: validatedPhones });
        return isValid;
    }, [state, createPartyData, updateState]);

    const buildPayload = useCallback(() => ({
        carrier_id: state.carrier_id,
        party_type: state.party_type,
        legal_name: state.legal_name.trim(),
        contact_name: state.contact_name.trim(),
        organization_name: state.organization_name.trim(),
        party_source: state.party_source,
        party_entity_type: state.party_entity_type,
        email: state.email.trim(),
        website: state.website.trim(),
        is_global: !state.is_global,
        contact_relationship: state.contact_relationship,
        mailing_address1: state.mailing_address1.trim(),
        mailing_address2: state.mailing_address2.trim(),
        mailing_city: state.mailing_city.trim(),
        mailing_state: state.mailing_state,
        mailing_zip: state.mailing_zip.trim(),
        mailing_country: state.mailing_country,
        billing_address_is_same: state.billing_address_is_same,
        billing_address1: state.billing_address_is_same ? "" : state.billing_address1.trim(),
        billing_address2: state.billing_address_is_same ? "" : state.billing_address2.trim(),
        billing_city: state.billing_address_is_same ? "" : state.billing_city.trim(),
        billing_state: state.billing_address_is_same ? "" : state.billing_state,
        billing_zip: state.billing_address_is_same ? "" : state.billing_zip.trim(),
        billing_country: state.billing_address_is_same ? "" : state.billing_country,
        tax_id: state.tax_id.trim(),
        account_number: state.account_number.trim(),
        phone_details: state.phone_details.filter(p => p.phone_type || p.phone.trim()).map(p => ({
            phone_type: p.phone_type,
            phone: p.phone.trim()
        })),
        successCallback: () => {
            onSuccess();
            closeForm();
        }
    }), [state, onSuccess, closeForm]);

    const submitForm = useCallback(() => {
        if (!validate()) return;

        updateState({ buttonLoading: true });
        const payload = buildPayload();

        if (editId) {
            const getChangedFields = (original = {}, updated = {}) => {
                return Object.keys(updated).reduce((acc, key) => {
                    if (key === "successCallback") {
                        acc[key] = updated[key];
                        return acc;
                    }
                    if (!isEqual(original[key], updated[key])) {
                        acc[key] = updated[key];
                    }
                    return acc;
                }, {});
            };
            const changedPayload = getChangedFields(originalPayloadRef.current, payload);
            dispatch(editAssociatedParty({ party_id: editId, successCallback: payload.successCallback, ...changedPayload }));
            return;
        }

        if (!isAdminPanel) {
            delete payload.carrier_id;
            delete payload.party_source;
            if (associateWith) {
                const isActivity = associateWith === "activity";
                payload.module_id = isActivity ? activityId : workkardId;
                payload.module = associateWith;
            }
        }
        dispatch(saveAssociatedParty(payload));
    }, [editId, isAdminPanel, associateWith, activityId, workkardId, validate, buildPayload, updateState, dispatch]);

    const handleCloseForm = useCallback(() => {
        if (state.touched) {
            dispatch(showDialogueBox({
                flag: true,
                component: <ConfirmationTextBox
                    message="You are trying to move away, all entered data will be lost."
                    onConfirmClick={() => { closeForm(); dispatch(hideDialogueBox()); }}
                    onCancelClick={() => dispatch(hideDialogueBox())}
                    confirmationText="Confirm"
                    cancelText="Cancel"
                />,
                overlayClassName: "modal",
                contentClassName: "modalContent small animated faster fadeInUpx2 confirmModal",
                shouldCloseOnOverlayClick: true
            }));
        } else {
            closeForm();
        }
    }, [state.touched, closeForm, dispatch]);

    const onClickPartySuggestion = useCallback((id) => {
        updateState({ showPartySuggestions: false, showContactSuggestions: false });
        dispatch(showDialogueBox({
            flag: true,
            component: <ConfirmationTextBox
                message="This Party already exists, do you wish to continue?"
                onConfirmClick={() => {
                    dispatch(editAssociatedParty({
                        id: id, flag: "edit",
                        entity_id: associateWith ? (associateWith === "activity" ? activityId : workkardId) : "",
                        entity_type: associateWith ? (associateWith === "activity" ? 'act' : 'wrk') : "",
                        _: Date.now(),
                    }));
                    dispatch(hideDialogueBox());
                }}
                onCancelClick={() => dispatch(hideDialogueBox())}
                confirmationText="Confirm"
                cancelText="Cancel"
                />,
                overlayClassName: "modal",
                contentClassName: "modalContent small animated faster fadeInUpx2",
                shouldCloseOnOverlayClick: true
        }));
    }, [associateWith, activityId, workkardId, updateState, dispatch]);

    const isPersonalOnly = state.partyId ? (!formData?.IsAssociatedOnly && !formData?.is_global) : (state.is_global && !associateWith);

    return (
        <div className="associatedParties">
            <div className="partyCreation scrollArea partyScroll animated faster fadeInRight">
                {/* Organization Name */}
                <div className={`formGroup colFlex ${(state.nameRequiredError || state.orgNameError) ? 'errorMsg' : ''}`}>
                    <label>Organization Name<sup className="required">*</sup></label>
                    <div ref={partySuggestionRef} className={`autoSuggestion ${state.showPartySuggestions && partySuggestions?.length ? "active" : ''}`}>
                        <input
                            onFocus={() => { updateState({ showPartySuggestions: true, nameRequiredError: false, orgNameError: false }); }}
                            onChange={handleChange}
                            value={state.organization_name}
                            name="organization_name" type="text" autoComplete="off" maxLength={200}
                        />
                        {(state.showPartySuggestions && partySuggestions?.length > 0) &&
                            <div className="searchResult autoSuggestionList">
                                <ul>
                                    {partySuggestions.map((party) => (
                                        party.organization_name ? <li key={party.PartyId}><a href="javascript:void(0)" onClick={() => onClickPartySuggestion(party.PartyId)}>{party.organization_name}</a></li> : null
                                    ))}
                                </ul>
                            </div>
                        }
                    </div>
                    {state.orgNameError && <span>{SPECIALCHARACTER_NAME_ERROR}</span>}
                </div>

                {/* Legal Name */}
                <div className={`formGroup colFlex ${state.legalNameError ? 'errorMsg' : ''}`}>
                    <label>Legal Name</label>
                    <input onChange={handleChange} onFocus={() => updateState({ legalNameError: false })} value={state.legal_name} name="legal_name" type="text" maxLength={60} />
                    {state.legalNameError && <span>{SPECIALCHARACTER_NAME_ERROR}</span>}
                </div>

                {/* Contact Name */}
                <div className={`formGroup colFlex ${(state.nameRequiredError || state.contactNameError) ? 'errorMsg' : ''}`}>
                    <label>Contact Name<sup className="required">*</sup></label>
                    <div ref={contactSuggestionRef} className={`autoSuggestion ${state.showContactSuggestions && partySuggestions?.length ? "active" : ""}`}>
                        <input
                            name="contact_name" type="text"
                            onFocus={() => { updateState({ showContactSuggestions: true, nameRequiredError: false, contactNameError: false }); }}
                            onChange={handleChange} value={state.contact_name} maxLength={200}
                        />
                        {(state.showContactSuggestions && partySuggestions?.length > 0) &&
                            <div className="searchResult autoSuggestionList">
                                <ul>
                                    {partySuggestions.map((party) => (
                                        party.contact_name ? <li key={party.PartyId}><a href="javascript:void(0)" onClick={() => onClickPartySuggestion(party.PartyId)}>{party.contact_name}</a></li> : null
                                    ))}
                                </ul>
                            </div>
                        }
                    </div>
                    {state.nameRequiredError && <span>{NAME_REQUIRED_ERROR}</span>}
                    {state.contactNameError && <span>{SPECIALCHARACTER_NAME_ERROR}</span>}
                </div>

                {/* Party Type */}
                <div className={`formGroup colFlex ${state.typeRequiredError ? 'errorMsg' : ''}`}>
                    <label>Party Type<sup className="required">*</sup></label>
                    <select name="party_type" value={state.party_type} onChange={handleChange} onFocus={() => updateState({ typeRequiredError: false })}>
                        <option value="0">Please Select</option>
                        {createPartyData.partyTypes?.map(data => (
                            <option key={data.id} value={data.value}>{data.value}</option>
                        ))}
                    </select>
                    {state.typeRequiredError && <span className="error-text">{TYPE_REQUIRED_ERROR}</span>}
                </div>

                {/* Contact Relationship */}
                <div className={`formGroup colFlex ${state.relationRequiredError ? 'errorMsg' : ''}`}>
                    <label>Contact Relationship</label>
                    <select disabled={state.disableContactRelation} className={state.disableContactRelation ? "disabled" : ""} name="contact_relationship" value={state.contact_relationship} onChange={handleChange} onFocus={() => updateState({ relationRequiredError: false })}>
                        <option value="">Please Select</option>
                        {createPartyData.partyRelationTypes?.map(data => (
                            <option key={data.id} value={data.value}>{data.value}</option>
                        ))}
                    </select>
                    {state.relationRequiredError && <span>{RELATION_REQUIRED_ERROR}</span>}
                </div>

                {/* Entity Type */}
                <div className="formGroup colFlex">
                    <label>Entity Type</label>
                    <select name="party_entity_type" value={state.party_entity_type} onChange={handleChange} >
                        <option value="">Please Select</option>
                        {createPartyData.partyEntityTypes?.map(data => (
                            <option key={data.id} value={data.value}>{data.value}</option>
                        ))}
                    </select>
                </div>

                {/* Tax ID */}
                <div className={`formGroup colFlex ${state.taxIdError ? 'errorMsg' : ''}`}>
                    <label>Tax ID</label>
                    <input type="text" onChange={handleChange} onFocus={() => updateState({ taxIdError: false })} value={state.tax_id} name="tax_id" maxLength={50} />
                    {state.taxIdError && <span>{TAXID_REQUIRED_ERROR}</span>}
                </div>

                {/* Account Number */}
                <div className="formGroup colFlex">
                    <label>Account Number</label>
                    <input type="text" onChange={handleChange} value={state.account_number} name="account_number" maxLength={50} />
                </div>

                {/* Admin Specific Fields */}
                {isAdminPanel && (
                    <>
                        <div className="formGroup colFlex">
                            <label>Carrier</label>
                            <select name="carrier_id" value={state.carrier_id} onChange={handleChange}>
                                <option value="">Please Select</option>
                                {carrierList?.map(data => (
                                    <option key={data.carrier_id} value={data.carrier_id}>{data.carrier_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="formGroup colFlex">
                            <label>Party Source</label>
                            <select name="party_source" value={state.party_source} onChange={handleChange}>
                                <option value="">Please Select</option>
                                {createPartyData.partySourceList?.map(data => (
                                    <option key={data.id} value={data.id}>{data.text}</option>
                                ))}
                            </select>
                        </div>
                    </>
                )}

                {/* Checkboxes */}
                {(state.is_global && !editId) && (
                    <>
                        {!isAdminPanel && (
                            <div className="formGroup colFlex">
                                <div className="checkboxArea">
                                    <CheckBox disabled={isPersonalOnly} item={{id:"addToMyParty", label:"Add to My Party", checked: isPersonalOnly || state.addToMyParty}} handleCheckbox={handleCheckBoxChange} />
                                </div>
                            </div>
                        )}
                        {(!isAdminPanel && associateWith) && (
                            <div className={`formGroup colFlex ${state.checkboxError ? 'errorMsg' : ''}`}>
                                <div className="checkboxArea">
                                    <CheckBox item={{id:"partyEntity", checked:state.partyEntity, label:`Add to ${associateWith}`}} handleCheckbox={handleCheckBoxChange} />
                                </div>
                                {state.checkboxError && <span>{associateWith === 'workkard' ? WORKKARD_CHECKBOX_REQUIRED_ERROR : ACTIVITY_CHECKBOX_REQUIRED_ERROR}</span>}
                            </div>
                        )}
                    </>
                )}

                {/* Address Section */}
                <header className={`alignMiddle rowSpaceBetween expand ${state.addressExpanded ? 'open' : ''}`} onClick={() => updateState({ addressExpanded: !state.addressExpanded })}>
                    Mailing/Billing Address <Icon name="chev" className="icon mr" />
                </header>
                <div className="faster animated fadeInDown" style={{ display: state.addressExpanded ? "block" : "none" }}>
                    <div className={`formGroup colFlex ${state.addressError ? 'errorMsg' : ''}`}>
                        <label>Mailing Address 1</label>
                        <input type="text" onFocus={() => updateState({ addressError: false })} onChange={handleChange} value={state.mailing_address1} name="mailing_address1" maxLength={200} />
                        {state.addressError && <span>{ADDRESS_REQUIRED_ERROR}</span>}
                    </div>
                    <div className="formGroup colFlex"><label>Mailing Address 2</label><input type="text" onChange={handleChange} value={state.mailing_address2} name="mailing_address2" maxLength={200} /></div>
                    <div className={`formGroup colFlex ${state.cityError ? 'errorMsg' : ''}`}>
                        <label>Mailing City</label>
                        <input type="text" onChange={handleChange} onFocus={() => updateState({ cityError: false })} value={state.mailing_city} name="mailing_city" maxLength={200} />
                        {state.cityError && <span>{CITY_REQUIRED_ERROR}</span>}
                    </div>
                    
                    <div className="formGroup colFlex">
                        <label>Mailing State</label>
                        {isCountryWithStates(state.mailing_country) ? (
                            <SelectSearch
                                id="mailing_state" 
                                options={partyStatesData} 
                                iconName="chev"
                                openOnClick={true}
                                error={state.stateError}
                                title={state.selectTitle || 'Select State'} 
                                selected={state.mailing_state}
                                onSelect={(obj) => updateState({ mailing_state: obj.Value, selectTitle: obj.DisplayName, stateError: false })}
                            />
                        ) : (
                            <input type="text" onFocus={() => updateState({ stateError: false })} onChange={handleChange} value={state.mailing_state} name="mailing_state" />
                        )}
                        {state.stateError && <span className='errorMsg'>{STATE_REQUIRED_ERROR}</span>}
                    </div>

                    <div className={`formGroup colFlex ${state.zipError ? 'errorMsg' : ''}`}>
                        <label>Mailing Zip</label>
                        <input type="text" onFocus={() => updateState({ zipError: false })} onChange={handleChange} value={state.mailing_zip} name="mailing_zip" />
                        {state.zipError && <span>{ZIP_REQUIRED_ERROR}</span>}
                    </div>

                    <div className="formGroup colFlex">
                        <label>Mailing Country</label>
                        <select name="mailing_country" value={state.mailing_country} onChange={handleChange}>
                            {countriesData.map(data => <option key={data.code} value={data.code}>{data.name}</option>)}
                        </select>
                    </div>

                    <div className="checkboxArea mb">
                        <CheckBox handleCheckbox={handleCheckBoxChange} item={{checked:state.billing_address_is_same, id:"billing_address_is_same", label:"Billing Address is same"}}/>
                    </div>

                    {!state.billing_address_is_same && (
                        <div>
                            <div className="formGroup colFlex"><label>Billing Address 1</label><input type="text" onChange={handleChange} value={state.billing_address1} name="billing_address1" maxLength={200} /></div>
                            <div className="formGroup colFlex"><label>Billing Address 2</label><input type="text" onChange={handleChange} value={state.billing_address2} name="billing_address2" maxLength={200} /></div>
                            <div className="formGroup colFlex"><label>Billing City</label><input type="text" onChange={handleChange} value={state.billing_city} name="billing_city" maxLength={200} /></div>
                            <div className="formGroup colFlex">
                                <label>Billing State</label>
                                {isCountryWithStates(state.billing_country) ? (
                                    <SelectSearch
                                        id="billing_state" options={partyStatesData} iconName="chev" openOnClick={true}
                                        title={state.selectBillingTitle || 'Select State'} selected={state.billing_state}
                                        onSelect={(obj) => updateState({ billing_state: obj.Value, selectBillingTitle: obj.DisplayName })}
                                    />
                                ) : (
                                    <input type="text" onChange={handleChange} value={state.billing_state} name="billing_state" />
                                )}
                            </div>
                            <div className="formGroup colFlex"><label>Billing Zip</label><input type="text" onChange={handleChange} value={state.billing_zip} name="billing_zip" maxLength={200} /></div>
                            <div className="formGroup colFlex">
                                <label>Billing Country</label>
                                <select name="billing_country" value={state.billing_country} onChange={handleChange}>
                                    {countriesData.map(data => <option key={data.code} value={data.code}>{data.name}</option>)}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Phone Details */}
                <header className={`alignMiddle rowSpaceBetween expand ${state.phoneExpanded ? 'open' : ''}`} onClick={() => updateState({ phoneExpanded: !state.phoneExpanded })}>
                    Phone Details <Icon name="chev" className="icon mr" />
                </header>
                <div className="faster animated fadeInDown phoneSelect" style={{ display: state.phoneExpanded ? "block" : "none" }}>
                    {state.phone_details.map((data, i) => (
                        <React.Fragment key={i}>
                            <div className={`formGroup colFlex ${data.typeError ? 'errorMsg' : ''}`}>
                                <label>Phone Type {i + 1}</label>
                                <select name="phone_type" value={data.phone_type || ""} onChange={(e) => handleChangePhone(e, i)}>
                                    <option value="">Select Type</option>
                                    {createPartyData.partyPhoneTypes?.map(pType => <option key={pType.phone_type} value={pType.phone_type}>{pType.phone_type}</option>)}
                                </select>
                                {data.typeError && <span>{data.typeError}</span>}
                            </div>
                            <div className={`formGroup colFlex ${data.numberError ? 'errorMsg' : ''}`}>
                                <label>Phone Number</label>
                                <input onChange={(e) => handleChangePhone(e, i)} value={data.phone} name="phone" type="text" />
                                {data.numberError && <span>{data.numberError}</span>}
                            </div>
                        </React.Fragment>
                    ))}
                    <a href="javascript:void(0)" onClick={() => updateState({
                        phone_details: [...state.phone_details, { ...EMPTY_PHONE }],
                        touched: true
                    })}> Add additional Phone </a>
                </div>

                {/* Web Details */}
                <header className={`alignMiddle rowSpaceBetween expand ${state.webExpanded ? 'open' : ''}`} onClick={() => updateState({ webExpanded: !state.webExpanded })}>
                    Web &amp; Email Details <Icon name="chev" className="icon mr" />
                </header>
                <div className="faster animated fadeInDown phoneSelect" style={{ display: state.webExpanded ? "block" : "none" }}>
                    <div className={`formGroup colFlex ${state.emailError ? 'errorMsg' : ''}`}>
                        <label>Email</label>
                        <input type="text" onChange={handleChange} onFocus={() => updateState({ emailError: false })} value={state.email} name="email" />
                        {state.emailError && <span>{EMAIL_ERROR}</span>}
                    </div>
                    <div className={`formGroup colFlex ${state.websiteError ? 'errorMsg' : ''}`}>
                        <label>Website</label>
                        <input type="text" onChange={handleChange} onFocus={() => updateState({ websiteError: false })} value={state.website} name="website" />
                        {state.websiteError && <span>{WEBSITE_ERROR}</span>}
                    </div>
                </div>
            </div>

            <footer className="rowFlex alignRight">
                <button className="btn buttonGradient" disabled={state.buttonLoading} onClick={submitForm}>
                    {state.buttonLoading && <LoaderComponent />}
                    <span style={{ visibility: state.buttonLoading ? "hidden" : "" }}>Save</span>
                </button>
                <button className="btn link" onClick={handleCloseForm}>Cancel</button>
            </footer>
        </div>
    );
};

export default CreateParty;